import type { ClientGame, ClientGameCtx } from "./index";
import { RING, FIGHTER, MATCH } from "../../shared/boxing";
import { sound } from "../sound";
import { roundRectPath, groundShadow } from "../draw";
import { C, TINT, SEAT_COLORS, displayFont } from "../theme";
import { countdownOverlay, safeInsets, hudLayout, playfield, controlsReserve } from "./asteroids";

interface RP { x: number; y: number; ang: number; punchN: number; punch: number; flash: number }

const STICK_RADIUS = 58; // px travel for full tilt
const DEADZONE = 0.14;

export class BoxingClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private stickBase!: HTMLDivElement;
  private stickNub!: HTMLDivElement;
  private punchBtn!: HTMLDivElement;

  private raf = 0;
  private inputTimer = 0;
  private last = performance.now();

  // input
  private held = new Set<string>();
  private move = { x: 0, y: 0 };
  private punchHeld = false;
  private stickId: number | null = null;
  private stickOrigin = { x: 0, y: 0 };

  private rp: RP[] = [
    { x: 0, y: 130, ang: -Math.PI / 2, punchN: 0, punch: 0, flash: 0 },
    { x: 0, y: -130, ang: Math.PI / 2, punchN: 0, punch: 0, flash: 0 },
  ];

  private onKeyDown = (e: KeyboardEvent) => this.key(e, true);
  private onKeyUp = (e: KeyboardEvent) => this.key(e, false);

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();

    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);

    this.stickBase = div("stick-base");
    this.stickNub = div("stick-nub");
    this.stickBase.appendChild(this.stickNub);
    this.stickBase.style.display = "none";
    ctx.root.appendChild(this.stickBase);

    this.punchBtn = div("punch-btn");
    this.punchBtn.textContent = "Punch";
    ctx.root.appendChild(this.punchBtn);
    bindHold(this.punchBtn, () => { this.punchHeld = true; sound.unlock(); }, () => (this.punchHeld = false));

    const hint = div("touch-hint");
    hint.textContent = "hold & drag anywhere to move";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 3500);

    ctx.root.addEventListener("touchstart", this.onTouchStart, { passive: false });
    ctx.root.addEventListener("touchmove", this.onTouchMove, { passive: false });
    ctx.root.addEventListener("touchend", this.onTouchEnd);
    ctx.root.addEventListener("touchcancel", this.onTouchEnd);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);

    ctx.room.onMessage("swing", () => sound.swing());
    ctx.room.onMessage("hit", (msg: any) => {
      sound.hit();
      const foe = msg?.bySlot === 0 ? 1 : 0;
      if (this.rp[foe]) this.rp[foe].flash = 200;
      try { navigator.vibrate?.(msg?.bySlot === ctx.mySlot ? 15 : 40); } catch { /* ignore */ }
    });

    this.inputTimer = window.setInterval(() => this.pushInput(), 33);
    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    clearInterval(this.inputTimer);
    const r = this.ctx.root;
    r.removeEventListener("touchstart", this.onTouchStart);
    r.removeEventListener("touchmove", this.onTouchMove);
    r.removeEventListener("touchend", this.onTouchEnd);
    r.removeEventListener("touchcancel", this.onTouchEnd);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  }

  results(ctx: ClientGameCtx) {
    const s0 = ctx.room.state.boxing?.fighters?.[0]?.score ?? 0;
    const s1 = ctx.room.state.boxing?.fighters?.[1]?.score ?? 0;
    return div("score-big", `${s0} : ${s1}`);
  }

  // ---- touch joystick ----

  private onTouchStart = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) {
      if (this.punchBtn.contains(t.target as Node)) continue;
      if ((t.target as HTMLElement)?.closest?.("button, a, input, [data-nojoy]")) continue;
      if (this.stickId !== null) continue;
      this.stickId = t.identifier;
      this.stickOrigin = { x: t.clientX, y: t.clientY };
      this.stickBase.style.left = `${t.clientX}px`;
      this.stickBase.style.top = `${t.clientY}px`;
      this.stickBase.style.display = "block";
      this.setNub(0, 0);
      sound.unlock();
      e.preventDefault();
    }
  };

  private onTouchMove = (e: TouchEvent) => {
    if (this.stickId === null) return;
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      let dx = t.clientX - this.stickOrigin.x;
      let dy = t.clientY - this.stickOrigin.y;
      const len = Math.hypot(dx, dy);
      const max = STICK_RADIUS;
      if (len > max) { dx = (dx / len) * max; dy = (dy / len) * max; }
      this.setNub(dx, dy);
      const raw = Math.min(1, len / max);
      const mag = raw <= DEADZONE ? 0 : (raw - DEADZONE) / (1 - DEADZONE);
      const ang = Math.atan2(dy, dx);
      this.move = { x: Math.cos(ang) * mag, y: Math.sin(ang) * mag };
      e.preventDefault();
    }
  };

  private onTouchEnd = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      this.stickId = null;
      this.move = { x: 0, y: 0 };
      this.stickBase.style.display = "none";
    }
  };

  private setNub(dx: number, dy: number) {
    this.stickNub.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  // ---- keyboard ----

  private key(e: KeyboardEvent, down: boolean) {
    const map: Record<string, string> = {
      ArrowUp: "up", KeyW: "up", ArrowDown: "down", KeyS: "down",
      ArrowLeft: "left", KeyA: "left", ArrowRight: "right", KeyD: "right",
    };
    if (map[e.code]) {
      down ? this.held.add(map[e.code]) : this.held.delete(map[e.code]);
      e.preventDefault();
    }
    if (e.code === "Space" || e.code === "KeyJ" || e.code === "Enter") {
      this.punchHeld = down;
      if (down) sound.unlock();
      e.preventDefault();
    }
  }

  private currentInput() {
    let mx = this.move.x;
    let my = this.move.y;
    if (this.stickId === null) {
      mx = (this.held.has("right") ? 1 : 0) - (this.held.has("left") ? 1 : 0);
      my = (this.held.has("down") ? 1 : 0) - (this.held.has("up") ? 1 : 0);
      const l = Math.hypot(mx, my) || 1;
      mx /= l; my /= l;
    }
    // slot 1 sees the ring turned 180 degrees, so their stick is mirrored too
    if (this.ctx.mySlot === 1) { mx = -mx; my = -my; }
    return { mx, my, punch: this.punchHeld };
  }

  private pushInput() {
    if (this.ctx.room.state.phase !== "playing") return;
    this.ctx.room.send("input", this.currentInput());
  }

  // ---- render ----

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    try { this.draw(dt); } catch (err) { console.error("boxing frame", err); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const { room, mySlot } = this.ctx;
    const canvas = this.canvas;
    const ctx = canvas.getContext("2d")!;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
    }
    ctx.save();
    ctx.scale(dpr, dpr);

    // ---------- HUD layout (drawn last) ----------
    const fs = room.state?.boxing?.fighters;
    const s0 = fs?.[0]?.score ?? 0;
    const s1 = fs?.[1]?.score ?? 0;
    const tl = room.state?.boxing?.timeLeft ?? MATCH.durationMs;
    const secs = Math.ceil(tl / 1000);
    const mm = Math.floor(secs / 60);
    const ss = String(secs % 60).padStart(2, "0");
    const ins = safeInsets();
    const hud = hudLayout(ctx, cw, ins.top + 8, [
      { text: String(s0), label: mySlot === 0 ? "you" : "rival", fill: TINT.coral },
      { text: `${mm}:${ss}` },
      { text: String(s1), label: mySlot === 1 ? "you" : "rival", fill: TINT.cobalt },
    ], room.state?.mode === "solo");

    // ---------- scene: lilac floor, edge to edge ----------
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, cw, ch);

    const spanW = RING.w + RING.border * 2;
    const spanH = RING.h + RING.border * 2;
    const field = playfield(cw, ch, hud.bottom + 12, ins.bottom + controlsReserve(cw, ch), spanW, spanH);
    const scale = field.scale;
    const cx = cw / 2;
    const cy = field.oy + (spanH * scale) / 2;
    const flip = mySlot === 1; // slot 1 sees the ring turned around: they're always at the bottom

    // ---------- ring: white apron, mint mat, coral ropes ----------
    const now = performance.now();
    const inW = RING.w / 2;
    const inH = RING.h / 2;
    const halfW = inW + RING.border;
    const halfH = inH + RING.border;
    const R = (v: number) => v * scale;

    groundShadow(ctx, cx, cy + R(halfH) * 0.96, R(halfW) * 1.05, R(halfH) * 0.08, 0.12);
    ctx.save();
    ctx.translate(cx, cy);
    if (flip) ctx.rotate(Math.PI);
    ctx.translate(-cx, -cy);
    // apron: rounded sticker with a hard ink shadow
    ctx.fillStyle = C.ink;
    roundRectPath(ctx, cx - R(halfW) + 4, cy - R(halfH) + 6, R(halfW) * 2, R(halfH) * 2, R(16));
    ctx.fill();
    ctx.fillStyle = C.white;
    roundRectPath(ctx, cx - R(halfW), cy - R(halfH), R(halfW) * 2, R(halfH) * 2, R(16));
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.strokeStyle = C.ink;
    ctx.stroke();
    // mat
    ctx.fillStyle = C.mint;
    roundRectPath(ctx, cx - R(inW), cy - R(inH), R(inW) * 2, R(inH) * 2, R(6));
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.22)";                 // centre ring mark
    ctx.beginPath();
    ctx.arc(cx, cy, R(inW * 0.42), 0, Math.PI * 2);
    ctx.fill();
    // ropes: ink under-stroke, coral on top
    const ropeW = Math.max(2.5, R(4));
    for (const k of [0.99, 1 + (RING.border * 0.26) / inW]) {
      const ox = inW * k;
      const oy = inH * (k === 0.99 ? 0.99 : 1 + (RING.border * 0.26) / inH);
      ctx.strokeStyle = C.ink;
      ctx.lineWidth = ropeW + 3;
      roundRectPath(ctx, cx - R(ox), cy - R(oy), R(ox) * 2, R(oy) * 2, R(5));
      ctx.stroke();
      ctx.strokeStyle = C.coral;
      ctx.lineWidth = ropeW;
      ctx.stroke();
    }
    // corner posts: mango sticker squares
    const post = Math.max(8, R(17));
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
      const px = cx + sx * R(inW + RING.border * 0.26) - post / 2;
      const py = cy + sy * R(inH + RING.border * 0.26) - post / 2;
      ctx.fillStyle = C.ink;
      roundRectPath(ctx, px + 2, py + 3, post, post, post * 0.3);
      ctx.fill();
      ctx.fillStyle = C.mango;
      roundRectPath(ctx, px, py, post, post, post * 0.3);
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = C.ink;
      ctx.stroke();
    }

    // ---------- boxers ----------
    for (let i = 0; i < 2; i++) {
      const s = fs?.[i];
      const rp = this.rp[i]!;
      if (s) {
        const k = Math.min(1, dt * 0.03);
        rp.x += (s.x - rp.x) * k;
        rp.y += (s.y - rp.y) * k;
        rp.ang = s.ang ?? rp.ang;
        rp.punchN = s.punchN ?? 0;
        rp.punch = s.punch;
        rp.flash = Math.max(rp.flash, s.hitFlash);
      }
      rp.flash = Math.max(0, rp.flash - dt);
      const stunned = (s?.stun ?? 0) > 0;
      drawBoxer(
        ctx, cx + rp.x * scale, cy + rp.y * scale, scale,
        rp.ang, rp.punch, rp.punchN, i, rp.flash, stunned, i === mySlot, flip, now,
      );
    }
    ctx.restore();

    // apron sign: JACE + green dot + GAMES (always right way up, at the bottom)
    {
      const fpx = Math.round(Math.max(10, R(15)));
      ctx.font = displayFont(fpx);
      ctx.textBaseline = "alphabetic";
      ctx.textAlign = "left";
      const wA = ctx.measureText("JACE").width;
      const wB = ctx.measureText("GAMES").width;
      const dot = fpx * 0.36;
      const gap = fpx * 0.16;
      const total = wA + gap + dot * 2 + gap + wB;
      const sy = cy + R(inH) + R(RING.border * 0.7);
      let x = cx - total / 2;
      ctx.fillStyle = C.ink;
      ctx.fillText("JACE", x, sy);
      x += wA + gap;
      ctx.beginPath();
      ctx.arc(x + dot, sy - dot * 0.95, dot, 0, Math.PI * 2);
      ctx.fillStyle = C.green;
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = C.ink;
      ctx.stroke();
      x += dot * 2 + gap;
      ctx.fillStyle = C.ink;
      ctx.fillText("GAMES", x, sy);
    }

    // ---------- HUD ----------
    hud.draw();

    if (room.state?.phase === "countdown") {
      countdownOverlay(ctx, cw, ch, room.state.countdown, { go: "Fight!", cy });
    }
    ctx.restore();
  }
}

function div(cls: string, text?: string) {
  const d = document.createElement("div");
  d.className = cls;
  if (text != null) d.textContent = text;
  return d;
}

/** A top-down boxer, drawn facing +x and rotated to `ang` (toward the foe):
 *  shoulders, a head with hair, and two gloved fists on guard that take turns
 *  jabbing out. Seat-coloured gloves and tank top, skin arms, ink outlines;
 *  flashes white when hit. (Mirrors the native app's Skia version.) */
function drawBoxer(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  scale: number,
  ang: number,
  punch: number,
  punchN: number,
  slot: number,
  flash: number,
  stunned: boolean,
  isMe: boolean,
  flip: boolean,
  now: number,
) {
  const U = FIGHTER.radius * scale; // one body radius in px
  const hit = flash > 0 && Math.floor(flash / 45) % 2 === 1;
  const seat = SEAT_COLORS[slot] ?? C.coral;
  const body = hit ? C.white : seat;
  const skin = hit ? C.white : slot % 2 ? "#C98F62" : "#F6CBA7";
  const hair = hit ? "#dddddd" : slot % 2 ? "#2A1B1D" : "#5A3A24";
  const glove = hit ? "#FFE3E0" : seat;
  const lw = Math.max(2, U * 0.11);
  const circle = (x: number, y: number, r: number) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); };

  // ground shadow
  ctx.fillStyle = "rgba(27,21,52,0.2)";
  circle(px, py + 3, U * 1.2);
  ctx.fill();

  const punching = punch > 0;
  const e = punching ? Math.sin(Math.min(1, punch) * Math.PI) : 0;
  const leftThrows = punchN % 2 === 1;
  const sway = stunned ? 0 : Math.sin(now / 230 + slot * 2.1);
  const hands = ([-1, 1] as const).map((s) => {
    const throwing = punching && (s === -1) === leftThrows;
    const guardX = U * (1.12 + (throwing ? 0 : punching ? -0.12 : 0.04 * sway * s));
    const gx = throwing ? guardX + (FIGHTER.reach * scale - guardX) * e : guardX;
    const gy = s * U * (throwing ? 0.7 - 0.58 * e : 0.7 + (punching ? 0.1 : 0.05 * sway * -s));
    return { s, gx, gy, throwing };
  });

  ctx.save();
  // a flinch when hit: drawn only, the fighter's real position never moves
  const recoil = (flash / 180) * 7 * scale;
  ctx.translate(px - Math.cos(ang) * recoil, py - Math.sin(ang) * recoil);
  ctx.rotate(ang + (stunned ? Math.sin(now / 70) * 0.18 : 0));
  const breathe = 1 + (stunned ? 0 : 0.025 * Math.sin(now / 210 + slot * 2.3));
  ctx.scale(breathe, breathe);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = C.ink;
  ctx.lineWidth = lw;

  // forearms, shoulder to glove
  for (const h of hands) {
    ctx.beginPath();
    ctx.moveTo(-U * 0.05, h.s * U * 1.08);
    ctx.lineTo(h.gx, h.gy);
    ctx.strokeStyle = C.ink;
    ctx.lineWidth = U * 0.62;
    ctx.stroke();
    ctx.strokeStyle = skin;
    ctx.lineWidth = U * 0.42;
    ctx.stroke();
  }
  ctx.strokeStyle = C.ink;
  ctx.lineWidth = lw;

  // shoulders / tank top
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(0, 0, U * 0.66, U * 1.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  circle(-U * 0.3, 0, U * 0.3);
  ctx.fill();

  // head: skin, with hair covering the crown so a sliver of forehead leads
  ctx.fillStyle = skin;
  circle(U * 0.1, 0, U * 0.52);
  ctx.fill();
  ctx.stroke();
  circle(U * 0.06, -U * 0.52, U * 0.14); ctx.fill();
  circle(U * 0.06, U * 0.52, U * 0.14); ctx.fill();
  ctx.fillStyle = hair;
  circle(-U * 0.04, 0, U * 0.42);
  ctx.fill();

  // gloves on top: cuff, then the padded fist
  for (const h of hands) {
    const gr = U * (h.throwing ? 0.52 : 0.45);
    ctx.fillStyle = C.white;
    circle(h.gx - U * 0.42, h.gy, U * 0.25);
    ctx.fill();
    ctx.lineWidth = lw * 0.8;
    ctx.stroke();
    ctx.lineWidth = lw;
    ctx.fillStyle = glove;
    circle(h.gx, h.gy, gr);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    circle(h.gx + gr * 0.2, h.gy - gr * 0.3, gr * 0.26);
    ctx.fill();
  }
  ctx.restore();

  // stun: stars orbiting the head
  if (stunned) {
    for (let k = 0; k < 3; k++) {
      const a = now / 150 + (k * Math.PI * 2) / 3;
      ctx.fillStyle = C.white;
      ctx.strokeStyle = C.ink;
      ctx.lineWidth = 1.4;
      circle(px + Math.cos(a) * U, py + Math.sin(a) * U, 3.2);
      ctx.fill();
      ctx.stroke();
    }
  }

  // "you" marker, on the screen-bottom side of your fighter
  if (isMe) {
    const m = flip ? -1 : 1;
    const my = py + m * U * 1.9;
    ctx.fillStyle = C.green;
    ctx.strokeStyle = C.ink;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, my - 8 * m);
    ctx.lineTo(px - 7, my + 5 * m);
    ctx.lineTo(px + 7, my + 5 * m);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

function bindHold(el: HTMLElement, on: () => void, off: () => void) {
  const start = (e: Event) => { e.preventDefault(); el.classList.add("active"); on(); };
  const end = (e: Event) => { e.preventDefault(); el.classList.remove("active"); off(); };
  el.addEventListener("touchstart", start, { passive: false });
  el.addEventListener("touchend", end, { passive: false });
  el.addEventListener("touchcancel", end, { passive: false });
  el.addEventListener("mousedown", start);
  el.addEventListener("mouseup", end);
  el.addEventListener("mouseleave", end);
}
